import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  type OnDestroy,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import {
  type IPractice,
  type IPracticeVerifoneSettings,
  VerifoneReceiptPrintMethod,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, snapshot, type WithRef } from '@principle-theorem/shared';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'pr-verifone-configuration',
  templateUrl: './verifone-configuration.component.html',
  styleUrls: ['./verifone-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VerifoneConfigurationComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _snackBar = inject(MatSnackBar);
  readonly receiptPrintMethod = VerifoneReceiptPrintMethod;
  form: TypedFormGroup<IPracticeVerifoneSettings>;
  practice$ = new ReplaySubject<WithRef<IPractice>>(1);

  @Input()
  set practice(practice: WithRef<IPractice>) {
    if (practice) {
      this.practice$.next(practice);
    }
  }

  constructor() {
    this.form = new TypedFormGroup<IPracticeVerifoneSettings>({
      isEnabled: new TypedFormControl<boolean>(false),
      testModeEnabled: new TypedFormControl<boolean>(false),
      receiptPrintMethod: new TypedFormControl<VerifoneReceiptPrintMethod>(
        VerifoneReceiptPrintMethod.Terminal
      ),
    });
    this.practice$.pipe(takeUntil(this._onDestroy$)).subscribe((practice) => {
      this.form.reset({
        isEnabled: false,
        testModeEnabled: false,
        receiptPrintMethod: VerifoneReceiptPrintMethod.Terminal,
      });
      this.form.patchValue(practice.verifoneSettings ?? {});
    });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    const practice = await snapshot(this.practice$);
    const verifoneSettings = this.form.getRawValue();
    await Firestore.patchDoc(practice.ref, { verifoneSettings });
    this._snackBar.open('Verifone settings saved');
  }
}
